import type {
  AiQueryInput,
  AiQueryResult,
  AiSessionSummary,
  AiSessionTranscript,
  AiStreamChunk,
} from "./types";

/**
 * AIAssistantClient — abstract interface for the supervisor agent.
 *
 * Two implementations:
 *  - `AgentEngineAssistantClient` (production): calls the proxy Cloud
 *    Function `/api/ai/query` which forwards to ADK / Vertex.
 *  - `MockAssistantClient` (dev/test): deterministic in-memory client.
 */
export interface AIAssistantClient {
  query(input: AiQueryInput): Promise<AiQueryResult>;
  queryStream(input: AiQueryInput): AsyncIterable<AiStreamChunk>;
  /** Past conversations of the current user (newest first). */
  listSessions(): Promise<ReadonlyArray<AiSessionSummary>>;
  /** Full transcript of a past session, to reopen it. */
  getSession(sessionId: string): Promise<AiSessionTranscript>;
  /** Permanently deletes one of the current user's conversations. */
  deleteSession(sessionId: string): Promise<void>;
}

/** Header the proxy reads the App Check token from (`functions/src/lib/app-check.ts`). */
const APP_CHECK_HEADER = "X-Firebase-AppCheck";

/**
 * Where proxy calls get their App Check token (C-16). The host app registers
 * it once at boot (`apps/web/src/lib/app-check.ts`), so the module that builds
 * the client doesn't need to know App Check exists. Unset (server-to-server,
 * tests) or a `null` token → no header: the proxy decides what that means.
 */
let appCheckTokenProvider: (() => Promise<string | null>) | null = null;

export function setAppCheckTokenProvider(provider: (() => Promise<string | null>) | null): void {
  appCheckTokenProvider = provider;
}

async function appCheckHeader(): Promise<Record<string, string>> {
  if (!appCheckTokenProvider) return {};
  try {
    const token = await appCheckTokenProvider();
    return token ? { [APP_CHECK_HEADER]: token } : {};
  } catch {
    // Never block the call on the client: without a token the proxy logs it
    // (switch off) or answers 401 app_check_failed (switch on).
    return {};
  }
}

/**
 * A non-2xx answer from the proxy. `code` is its `{ error }` body when it has
 * one — e.g. "app_check_failed", which the chat shows as its own message.
 */
export class AssistantRequestError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(operation: string, status: number, code: string | null) {
    super(`AgentEngineAssistantClient.${operation} failed: ${status}`);
    this.name = "AssistantRequestError";
    this.status = status;
    this.code = code;
  }
}

async function requestError(operation: string, res: Response): Promise<AssistantRequestError> {
  const body = (await res.json().catch(() => null)) as { error?: unknown } | null;
  return new AssistantRequestError(
    operation,
    res.status,
    typeof body?.error === "string" ? body.error : null,
  );
}

interface AgentEngineAssistantClientOpts {
  /**
   * Base URL of the proxy. Empty string (default) is the same-origin
   * Firebase Hosting rewrite path — the web bundle calls "/api/ai/…"
   * directly and Hosting routes it to the `aiApi` Cloud Function.
   */
  readonly baseUrl: string;
  /** Resolves the current Firebase ID token for the Bearer header. */
  readonly getAuthToken: () => Promise<string>;
}

/**
 * Production client: hits the Cloud Function proxy that forwards to ADK
 * / Vertex Agent Engine. The proxy is the single trust boundary —
 * extracts tenantId from the verified ID token, enforces RBAC,
 * rate-limits, writes the audit log, and calls the agent. The web app
 * is intentionally thin: this client just shapes the HTTP request and
 * decodes the response.
 *
 * Streaming uses NDJSON over the same POST (one JSON object per line);
 * `queryStream` parses the response body progressively so the chat UI
 * can render text as it arrives.
 */
export class AgentEngineAssistantClient implements AIAssistantClient {
  private readonly baseUrl: string;
  private readonly getAuthToken: () => Promise<string>;

  constructor(opts: AgentEngineAssistantClientOpts) {
    this.baseUrl = opts.baseUrl;
    this.getAuthToken = opts.getAuthToken;
  }

  async listSessions(): Promise<ReadonlyArray<AiSessionSummary>> {
    const token = await this.getAuthToken();
    const res = await fetch(`${this.baseUrl}/api/ai/sessions`, {
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
    });
    if (!res.ok) {
      throw await requestError("listSessions", res);
    }
    const body = (await res.json()) as { sessions: ReadonlyArray<AiSessionSummary> };
    return body.sessions;
  }

  async getSession(sessionId: string): Promise<AiSessionTranscript> {
    const token = await this.getAuthToken();
    const res = await fetch(`${this.baseUrl}/api/ai/sessions/${encodeURIComponent(sessionId)}`, {
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
    });
    if (!res.ok) {
      throw await requestError("getSession", res);
    }
    return (await res.json()) as AiSessionTranscript;
  }

  async deleteSession(sessionId: string): Promise<void> {
    const token = await this.getAuthToken();
    const res = await fetch(`${this.baseUrl}/api/ai/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}`, ...(await appCheckHeader()) },
    });
    if (!res.ok) {
      throw await requestError("deleteSession", res);
    }
  }

  async query(input: AiQueryInput): Promise<AiQueryResult> {
    const token = await this.getAuthToken();
    const res = await fetch(`${this.baseUrl}/api/ai/query`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        ...(await appCheckHeader()),
      },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      throw await requestError("query", res);
    }
    return (await res.json()) as AiQueryResult;
  }

  async *queryStream(input: AiQueryInput): AsyncIterable<AiStreamChunk> {
    const token = await this.getAuthToken();
    const res = await fetch(`${this.baseUrl}/api/ai/query/stream`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "text/event-stream",
        ...(await appCheckHeader()),
      },
      body: JSON.stringify(input),
    });
    if (!res.ok) {
      throw await requestError("queryStream", res);
    }
    if (!res.body) {
      throw new Error("AgentEngineAssistantClient.queryStream: no response body");
    }

    // Server-Sent Events: each message is one or more `data: <line>`
    // lines followed by a blank line. We only encode single-line JSON
    // payloads on the server, but the parser still scans for the
    // empty-line delimiter so a multi-line `data:` block would work
    // unchanged. Unknown SSE fields (`id:`, `event:`, `retry:`) are
    // silently ignored.
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";
    for (;;) {
      const { value, done } = await reader.read();
      if (value) buffer += decoder.decode(value, { stream: true });

      let sepIdx = buffer.indexOf("\n\n");
      while (sepIdx >= 0) {
        const block = buffer.slice(0, sepIdx);
        buffer = buffer.slice(sepIdx + 2);
        const dataLines = block
          .split("\n")
          .filter((l) => l.startsWith("data:"))
          .map((l) => l.slice(5).trimStart());
        if (dataLines.length > 0) {
          const payload = dataLines.join("\n");
          if (payload.length > 0) {
            yield JSON.parse(payload) as AiStreamChunk;
          }
        }
        sepIdx = buffer.indexOf("\n\n");
      }
      if (done) return;
    }
  }
}
